import { IconChevronDown } from '@developerz/ui';
import { type JSX, Show } from 'solid-js';
import { routePinTriggerValue } from '../lib/agents';
import type { RouteWire } from '../lib/llmRoutes';
import { AgentRepoModeRow } from './AgentRepoModeRow';
import type { AgentRepoOverridesSectionProps } from './AgentRepoOverridesSection';
import { AgentRouteAdvancedFields } from './AgentRouteAdvancedFields';
import { ModelPicker, pinFace } from './ModelPicker';
import { ModelSaveNotice } from './ModelSaveNotice';

// One repo's override on the Per-repo section: a row for a route this purpose pins at
// that repo's scope (#5211, #5436).
//
// FULL (the drawer's own section): the repo name, then the SAME `ModelPicker` the card
// uses, the Advanced fields, the save notice and — on a mode-backed agent — that repo's
// mode row, always open.
//
// COMPACT (the Default card, #5626): ONE line, "{repo} . {model}", a button that opens
// that row in place to the same controls. The model is the picker's own face
// (`pinFace`), so a pin reads one way in the line and in the trigger. There is no Remove
// control here by design: clearing an override is the expanded picker's Automatic
// option (`nextRouteRow` deletes the row once all three pin axes are null). The save
// notice stays OUTSIDE the collapsed part so a refusal still waiting on an answer
// survives a collapse.

/** What a row needs from its section — everything but the list, the title and the add slot. */
export type RepoOverrideContext = Pick<
  AgentRepoOverridesSectionProps,
  | 'agentName'
  | 'purpose'
  | 'repoNames'
  | 'keys'
  | 'canEdit'
  | 'saves'
  | 'modeKey'
  | 'canEditModes'
  | 'token'
  | 'onKeyRenamed'
>;

export type AgentRepoOverrideRowProps = {
  readonly route: RouteWire;
  readonly ctx: RepoOverrideContext;
  readonly compact: boolean;
  /** Whether a COMPACT row is expanded. Owned by the section, keyed by repo: every write
   * ends in a refetch that hands this row back as a NEW `RouteWire`, `<For>` re-creates
   * it, and a signal local to the row would reset to closed under the user's hands. */
  readonly open: boolean;
  readonly onToggle: () => void;
};

export function AgentRepoOverrideRow(props: AgentRepoOverrideRowProps): JSX.Element {
  const repoId = (): string => props.route.scope_ref;
  const name = (): string => props.ctx.repoNames.get(repoId()) ?? repoId();
  const busy = (): boolean => props.ctx.saves.isBusy(repoId(), props.ctx.purpose);
  const panelId = (): string => `agent-drawer-repo-${props.ctx.purpose}-${repoId()}-panel`;
  const choose = (change: Parameters<typeof props.ctx.saves.choose>[3]): void =>
    void props.ctx.saves.choose(repoId(), props.ctx.purpose, props.route, change);

  const controls = (): JSX.Element => (
    <>
      <ModelPicker
        agentName={props.ctx.agentName}
        purpose={props.ctx.purpose}
        route={props.route}
        pinValue={routePinTriggerValue(props.route)}
        keys={props.ctx.keys}
        canEdit={props.ctx.canEdit}
        busy={busy()}
        onChoose={choose}
      />
      <AgentRouteAdvancedFields
        idPrefix={`agent-drawer-repo-${props.ctx.purpose}-${repoId()}`}
        route={props.route}
        keys={props.ctx.keys}
        canEdit={props.ctx.canEdit}
        busy={busy()}
        onChange={choose}
        token={props.ctx.token}
        onKeyRenamed={props.ctx.onKeyRenamed}
      />
    </>
  );
  const notice = (): JSX.Element => (
    <ModelSaveNotice
      pending={props.ctx.saves.refusal(repoId(), props.ctx.purpose)}
      busy={busy()}
      onApplyAnyway={() => void props.ctx.saves.applyAnyway(repoId(), props.ctx.purpose)}
      onApplyReset={() => void props.ctx.saves.applyWithoutEffort(repoId(), props.ctx.purpose)}
      onDismiss={() => props.ctx.saves.dismiss(repoId(), props.ctx.purpose)}
    />
  );
  const modeRow = (): JSX.Element => (
    <Show when={props.ctx.modeKey !== undefined && props.ctx.canEditModes}>
      <AgentRepoModeRow
        repoId={repoId()}
        modeKey={props.ctx.modeKey as NonNullable<typeof props.ctx.modeKey>}
        token={props.ctx.token}
      />
    </Show>
  );

  return (
    <li class="agent-drawer__repo-row">
      <Show
        when={props.compact}
        fallback={
          <>
            <p class="agent-drawer__repo-name">{name()}</p>
            {controls()}
            {notice()}
            {modeRow()}
          </>
        }
      >
        <button
          type="button"
          class="agent-drawer__repo-summary"
          aria-expanded={props.open}
          aria-controls={panelId()}
          onClick={() => props.onToggle()}
        >
          <span class="agent-drawer__repo-summary-name">{name()}</span>
          {' · '}
          <span class="agent-drawer__repo-summary-model">
            {pinFace(props.route, routePinTriggerValue(props.route)).primary}
          </span>
          <IconChevronDown class="agent-drawer__repo-summary-chevron" size="0.875rem" />
        </button>
        <Show when={props.open}>
          <div class="agent-drawer__repo-panel" id={panelId()}>
            {controls()}
            {modeRow()}
          </div>
        </Show>
        {notice()}
      </Show>
    </li>
  );
}
